/**
 * Misconception Registry — Central catalog of known misconceptions.
 *
 * PEDAGOGICAL_CONTRACT_SPEC §10.2 — Centralized registry with module-prefix
 * convention (rek.*, spr.*, etc.) to prevent synonyms and enable governance
 * validation of misconception tags in seeds.
 *
 * Usage:
 * - Seed authors reference misconception IDs from this registry
 * - Governance pipeline validates that tags in seeds exist in this registry
 * - Admin UI can display human-readable labels for misconception tags
 */

export interface MisconceptionDefinition {
  /** Machine-readable ID (e.g., 'rek.carry-forgotten') */
  id: string
  /** Human-readable label (Dutch) */
  label: string
  /** Module scope */
  module: string
  /** Brief description of the misconception */
  description: string
  /** Common question families where this misconception appears */
  typicalFamilies?: string[]
}

/**
 * Central misconception registry.
 * Organized by module prefix for clarity.
 */
export const MISCONCEPTION_REGISTRY: MisconceptionDefinition[] = [
  // ── Basisrekenen (REK) ──────────────────────────────────────────────────
  {
    id: 'rek.carry-forgotten',
    label: 'Onthouden vergeten',
    module: 'Basisrekenen',
    description:
      'Leerling vergeet de overdracht (carry) bij optellen of vermenigvuldigen.',
    typicalFamilies: ['REK-CIJF-PLUS', 'REK-CIJF-KEER'],
  },
  {
    id: 'rek.borrow-forgotten',
    label: 'Lenen vergeten',
    module: 'Basisrekenen',
    description:
      'Leerling vergeet te lenen bij aftrekken, trekt kleinste van grootste af per kolom.',
    typicalFamilies: ['REK-CIJF-MIN'],
  },
  {
    id: 'rek.order-of-operations',
    label: 'Volgorde verkeerd',
    module: 'Basisrekenen',
    description:
      'Leerling past de bewerkingsvolgorde (Meneer Van Dansen) niet correct toe.',
    typicalFamilies: ['REK-VOLGORDE'],
  },
  {
    id: 'rek.multiplication-table-error',
    label: 'Tafelfout',
    module: 'Basisrekenen',
    description:
      'Leerling kent een specifieke tafel niet uit het hoofd (bijv. 7×8=54).',
    typicalFamilies: ['REK-TAFELS-BASIS', 'REK-TAFELS-UITBREIDEN'],
  },
  {
    id: 'rek.division-remainder-ignored',
    label: 'Rest genegeerd',
    module: 'Basisrekenen',
    description:
      'Leerling negeert de rest bij een deling of rondt verkeerd af.',
    typicalFamilies: ['REK-CIJF-DEEL'],
  },

  // ── Getalbegrip (GET) ───────────────────────────────────────────────────
  {
    id: 'get.place-value-swap',
    label: 'Plaatswaarde verwisseld',
    module: 'Getalbegrip',
    description:
      'Leerling verwisselt tientallen en eenheden, of honderdtallen en tientallen.',
    typicalFamilies: ['GET-GROOT-LEZEN', 'GET-GROOT-POS'],
  },
  {
    id: 'get.decimal-align',
    label: 'Komma niet uitgelijnd',
    module: 'Getalbegrip',
    description:
      'Leerling lijnt kommagetallen niet uit bij optellen/aftrekken.',
    typicalFamilies: ['GET-KOMMA-PLUS', 'GET-KOMMA-MIN'],
  },
  {
    id: 'get.fraction-whole-comparison',
    label: 'Breuk als geheel getal vergeleken',
    module: 'Getalbegrip',
    description:
      'Leerling vergelijkt teller en noemer apart i.p.v. de breukwaarde.',
    typicalFamilies: ['GET-BREUK-BASIS', 'GET-VERGELIJK'],
  },
  {
    id: 'get.percentage-of-vs-increase',
    label: 'Percentage van vs. toename',
    module: 'Getalbegrip',
    description:
      'Leerling verwart "X% van" met "X% toename" (bijv. 20% van 100 vs. 100 + 20%).',
    typicalFamilies: ['GET-PROC-VAN', 'GET-PROC-TOENAME'],
  },
  {
    id: 'get.negative-subtraction-direction',
    label: 'Negatief aftrekken omgedraaid',
    module: 'Getalbegrip',
    description:
      'Leerling draait de richting om bij aftrekken met negatieve getallen.',
    typicalFamilies: ['GET-NEGATIEF-MIN'],
  },

  // ── Spelling Regels (SPR) ───────────────────────────────────────────────
  {
    id: 'spr.dt-confusion',
    label: 'D/T-fout',
    module: 'SpellingRegel',
    description:
      'Leerling past de d/t-regel verkeerd toe bij werkwoordsvormen.',
    typicalFamilies: ['SPR-WEET-D-T', 'SPR-WEET-DT'],
  },
  {
    id: 'spr.double-consonant',
    label: 'Verdubbeling vergeten',
    module: 'SpellingRegel',
    description:
      'Leerling vergeet de medeklinkerverdubbeling (bijv. "baken" i.p.v. "bakken").',
    typicalFamilies: ['SPR-KLANK-BAKKER', 'SPR-KLANK-VERDUBBEL'],
  },
  {
    id: 'spr.ei-ij-confusion',
    label: 'Ei/ij verwisseld',
    module: 'SpellingRegel',
    description: 'Leerling verwisselt ei en ij in woorden.',
    typicalFamilies: ['SPR-WEET-EI-IJ'],
  },
  {
    id: 'spr.open-closed-syllable',
    label: 'Open/gesloten lettergreep',
    module: 'SpellingRegel',
    description:
      'Leerling herkent het verschil niet tussen open en gesloten lettergrepen.',
    typicalFamilies: ['SPR-KLANK-JAGER', 'SPR-KLANK-VERKORTEN'],
  },
  {
    id: 'spr.au-ou-confusion',
    label: 'Au/ou verwisseld',
    module: 'SpellingRegel',
    description: 'Leerling verwisselt au en ou in woorden.',
    typicalFamilies: ['SPR-KLANK-AU-OU'],
  },

  // ── Spelling Werkwoorden (SWW) ──────────────────────────────────────────
  {
    id: 'sww.kofschip-misapplied',
    label: 'Kofschip verkeerd toegepast',
    module: 'SpellingWW',
    description:
      'Leerling past de kofschip-regel verkeerd toe bij voltooid deelwoord.',
    typicalFamilies: ['SWW-VD-KOF', 'SWW-VT-ZWAK-KOF'],
  },
  {
    id: 'sww.stem-extraction-error',
    label: 'Stam verkeerd bepaald',
    module: 'SpellingWW',
    description:
      'Leerling bepaalt de stam van het werkwoord verkeerd (bijv. "leeft" → stam "leef" i.p.v. "leev").',
    typicalFamilies: ['SWW-TT-STAM', 'SWW-TT-DSTAM'],
  },
  {
    id: 'sww.past-participle-ge-omitted',
    label: 'Ge- weggelaten',
    module: 'SpellingWW',
    description:
      'Leerling vergeet ge- bij het voltooid deelwoord (of voegt het onterecht toe).',
    typicalFamilies: ['SWW-VD-ZONDER-GE'],
  },
  {
    id: 'sww.irregular-verb-regularized',
    label: 'Onregelmatig werkwoord vervoegd als regelmatig',
    module: 'SpellingWW',
    description:
      'Leerling past regelmatige vervoegingsregels toe op een onregelmatig werkwoord.',
    typicalFamilies: ['SWW-VT-ONREGELMATIG', 'SWW-VD-STERK'],
  },

  // ── Metriek (MET) ──────────────────────────────────────────────────────
  {
    id: 'met.unit-factor-10',
    label: 'Factor 10 fout',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt of deelt met de verkeerde factor van 10 bij eenheidsomrekening.',
    typicalFamilies: ['MET-LEN-OMREKEN', 'MET-GEW-OMREKEN', 'MET-INH-OMREKEN'],
  },
  {
    id: 'met.area-perimeter-confused',
    label: 'Oppervlakte/omtrek verward',
    module: 'Metriek',
    description:
      'Leerling verwart oppervlakte met omtrek (bijv. l×b i.p.v. 2×(l+b)).',
    typicalFamilies: ['MET-OPP-RECHT', 'MET-OMTREK-RECHT'],
  },
  {
    id: 'met.scale-direction',
    label: 'Schaal verkeerde richting',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt i.p.v. deelt (of omgekeerd) bij schaalberekeningen.',
    typicalFamilies: ['MET-SCHAAL-BASIS', 'MET-SCHAAL-LENGTE'],
  },
  {
    id: 'met.angle-type-confused',
    label: 'Hoeksoort verward',
    module: 'Metriek',
    description:
      'Leerling verwart scherpe, stompe, rechte of gestrekte hoeken (bijv. 55° als stomp bestempeld).',
    typicalFamilies: ['MET-HOEK-SOORTEN'],
  },
  {
    id: 'met.angle-rotation-confused',
    label: 'Draaihoek verward',
    module: 'Metriek',
    description:
      'Leerling verwart kwart/halve/hele draai met het verkeerde aantal graden (bijv. kwart draai = 180°).',
    typicalFamilies: ['MET-HOEK-SOORTEN', 'MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.scale-unit-conversion',
    label: 'Eenheid vergeten om te rekenen bij schaal',
    module: 'Metriek',
    description:
      'Leerling vergeet cm naar m of km om te rekenen na de schaalberekening.',
    typicalFamilies: ['MET-SCHAAL-BASIS', 'MET-SCHAAL-LENGTE'],
  },
  {
    id: 'met.scale-area-not-squared',
    label: 'Schaalfactor niet gekwadrateerd bij oppervlakte',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt oppervlakte met de schaalfactor i.p.v. met het kwadraat ervan. Bijv. ×100 i.p.v. ×10.000 bij schaal 1:100.',
    typicalFamilies: ['MET-SCHAAL-OPP'],
  },
  {
    id: 'met.symmetry-line-count',
    label: 'Aantal symmetrielijnen verward',
    module: 'Metriek',
    description:
      'Leerling verwart het aantal symmetrielijnen van verschillende figuren (bijv. vierkant=4, rechthoek=2, gelijkzijdige driehoek=3).',
    typicalFamilies: ['MET-SYMMETRIE'],
  },
  {
    id: 'met.symmetry-mirror-axis',
    label: 'Spiegelas verward',
    module: 'Metriek',
    description:
      'Leerling spiegelt over de verkeerde as (bijv. horizontaal i.p.v. verticaal) of verwart x- en y-coördinaat bij spiegeling.',
    typicalFamilies: ['MET-SYMMETRIE'],
  },

  // ── Tijd (TIJD) ─────────────────────────────────────────────────────────
  {
    id: 'tijd.speed-formula-confused',
    label: 'Snelheidsformule verward',
    module: 'Tijd',
    description:
      'Leerling gebruikt de verkeerde bewerking (bijv. vermenigvuldigen in plaats van delen) bij de AST-driehoek.',
    typicalFamilies: ['TIJD-SNELHEID', 'TIJD-AFSTAND', 'TIJD-GEMIDDELDE'],
  },
  {
    id: 'tijd.minutes-to-hours-error',
    label: 'Minuten naar uren fout',
    module: 'Tijd',
    description:
      'Leerling rekent minuten verkeerd om naar decimale uren (bijv. 15 min = 0,15 uur in plaats van 0,25 uur).',
    typicalFamilies: ['TIJD-SNELHEID', 'TIJD-AFSTAND', 'TIJD-GEMIDDELDE'],
  },
  {
    id: 'tijd.timezone-direction-confused',
    label: 'Tijdzone richting verward',
    module: 'Tijd',
    description:
      'Leerling telt uren op in plaats van af te trekken bij tijdzones, of andersom.',
    typicalFamilies: ['TIJD-TIJDZONES'],
  },
  {
    id: 'tijd.timezone-midnight-error',
    label: 'Tijdzone over middernacht',
    module: 'Tijd',
    description:
      'Leerling houdt geen rekening met de 24-uurs grens bij tijdzones.',
    typicalFamilies: ['TIJD-TIJDZONES'],
  },
  {
    id: 'tijd.timezone-flight-error',
    label: 'Tijdverschil vergeten bij vlucht',
    module: 'Tijd',
    description:
      'Leerling vergeet het tijdverschil op te tellen of af te trekken bij het berekenen van de aankomsttijd na een vlucht.',
    typicalFamilies: ['TIJD-TIJDZONES'],
  },

  // ── Geld (GELD) ─────────────────────────────────────────────────────────
  {
    id: 'geld.decimal-comma',
    label: 'Komma vergeten/verkeerd',
    module: 'Geld',
    description:
      'Leerling plaatst de decimale komma verkeerd bij geldbedragen.',
    typicalFamilies: ['TGL-GELD-KOMMA'],
  },
  {
    id: 'geld.change-calculation',
    label: 'Wisselgeld verkeerd berekend',
    module: 'Geld',
    description:
      'Leerling berekent wisselgeld verkeerd (trekt van het verkeerde bedrag af).',
    typicalFamilies: ['TGL-GELD-TERUG', 'TGL-GELD-TERUGREKENEN'],
  },
  {
    id: 'geld.total-vs-unit-price',
    label: 'Totaalprijs verward met eenheidsprijs',
    module: 'Geld',
    description:
      'Leerling vergelijkt totaalprijzen in plaats van de prijs per eenheid (per stuk, per kg, per liter).',
    typicalFamilies: ['TGL-GELD-KOOPJES', 'TGL-GELD-PRIJS-PER'],
  },
  {
    id: 'geld.wrong-division-direction',
    label: 'Verkeerde deelrichting bij eenheidsprijs',
    module: 'Geld',
    description:
      'Leerling deelt het aantal door de prijs in plaats van de prijs door het aantal.',
    typicalFamilies: ['TGL-GELD-KOOPJES', 'TGL-GELD-PRIJS-PER'],
  },

  // ── Taalbegrip (TAA) ────────────────────────────────────────────────────
  {
    id: 'taa.main-idea-vs-detail',
    label: 'Hoofdgedachte vs. detail',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een ondersteunend detail met de hoofdgedachte van een tekst.',
    typicalFamilies: ['TAA-LEZEN-KERN', 'TAA-LEZEN-DETAIL'],
  },
  {
    id: 'taa.fact-vs-opinion',
    label: 'Feit vs. mening',
    module: 'Taalbegrip',
    description:
      'Leerling kan geen onderscheid maken tussen feiten en meningen in een tekst.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },
  {
    id: 'taa.subject-predicate-confused',
    label: 'Onderwerp/gezegde verward',
    module: 'Taalbegrip',
    description: 'Leerling verwart het onderwerp met het gezegde in een zin.',
    typicalFamilies: ['TAA-ONT-GEZEGDE', 'TAA-ONT-BASIS'],
  },
]

// ─── Lookup helpers ───────────────────────────────────────────────────────────

const _byId = new Map<string, MisconceptionDefinition>()
for (const m of MISCONCEPTION_REGISTRY) {
  _byId.set(m.id, m)
}

/** Get a misconception definition by ID. Returns undefined if not found. */
export function getMisconception(
  id: string,
): MisconceptionDefinition | undefined {
  return _byId.get(id)
}

/** Check if a misconception ID exists in the registry. */
export function isKnownMisconception(id: string): boolean {
  return _byId.has(id)
}

/** Get all misconceptions for a given module. */
export function getMisconceptionsForModule(
  module: string,
): MisconceptionDefinition[] {
  return MISCONCEPTION_REGISTRY.filter((m) => m.module === module)
}

/** Get all unique misconception IDs. */
export function getAllMisconceptionIds(): string[] {
  return MISCONCEPTION_REGISTRY.map((m) => m.id)
}
